import { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Send, X } from 'lucide-react';
import emailjs from '@emailjs/browser';

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    serviceType: '',
    dateNeeded: '',
    pickupLocation: '',
    destination: '',
    additionalDetails: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.serviceType) newErrors.serviceType = 'Please select a service type';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);

    try {
      const requestId = Math.random().toString(36).substr(2, 8).toUpperCase();
      const submissionTime = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });

      const emailData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        serviceType: formData.serviceType,
        dateNeeded: formData.dateNeeded || '',
        pickupLocation: formData.pickupLocation || '',
        destination: formData.destination || '',
        additionalDetails: formData.additionalDetails || '',
        requestId,
        submissionTime,
      };

      const response = await emailjs.send(
        'service_me2mq3r',
        'template_7tuq8mu',
        emailData,
        'KOa-rqKVkxVB1RKaM'
      );

      if (response.status === 200) {
        setToastMessage('Success! Your request has been submitted. We\'ll contact you soon.');
        setToastType('success');
        setShowToast(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          serviceType: '',
          dateNeeded: '',
          pickupLocation: '',
          destination: '',
          additionalDetails: '',
        });
        setErrors({});
      } else {
        throw new Error('Failed to submit request');
      }
    } catch (error) {
      console.error('EmailJS error:', error);
      setToastMessage('Error: There was an issue submitting your request. Please try again.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleServiceTypeChange = (e) => {
    setFormData({ ...formData, serviceType: e.target.value });
    setErrors({ ...errors, serviceType: '' });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 max-w-sm w-full animate-fade-in-up">
          <div
            className={`bg-deep-black text-white p-4 rounded-lg shadow-lg border-l-4 ${
              toastType === 'success' ? 'border-luxury-gold' : 'border-red-500'
            } flex items-center justify-between`}
          >
            <div className="flex items-center space-x-2">
              {toastType === 'success' ? (
                <svg className="h-6 w-6 text-luxury-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <p className="font-playfair text-sm">{toastMessage}</p>
            </div>
            <button onClick={() => setShowToast(false)} className="text-gray-300 hover:text-luxury-gold">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="py-20 animate-fade-in-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-playfair font-bold mb-6">
            Get In <span className="text-luxury-gold">Touch</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-in-up delay-100">
            Ready to experience luxury transportation? Contact us to make a reservation or get a customized quote for your needs.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8 animate-fade-in-up delay-200">
              <div>
                <h2 className="text-2xl font-playfair font-semibold mb-6 text-luxury-gold">Contact Information</h2>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <Phone className="text-luxury-gold h-6 w-6 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Phone</div>
                      <div className="text-gray-300">
                        <a href="tel:+12814286921" className="hover:text-luxury-gold transition-colors duration-300">(281) 428-6921</a>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Mail className="text-luxury-gold h-6 w-6 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Email</div>
                      <div className="text-gray-300">
                        <a href="mailto:black@pinnaclepremierlimo.com" className="hover:text-luxury-gold transition-colors duration-300">black@pinnaclepremierlimo.com</a>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <MapPin className="text-luxury-gold h-6 w-6 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Address</div>
                      <div className="text-gray-300">Houston & Richmond, TX</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Clock className="text-luxury-gold h-6 w-6 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Hours</div>
                      <div className="text-gray-300">24/7 Service Available</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4 text-luxury-gold">Follow Us</h3>
                <div className="flex space-x-4">
                  <a href="https://facebook.com" className="bg-luxury-gold text-deep-black w-10 h-10 rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors duration-300">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href="https://instagram.com" className="bg-luxury-gold text-deep-black w-10 h-10 rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors duration-300">
                    <Instagram className="h-5 w-5" />
                  </a>
                </div>
              </div>

              <div className="bg-luxury-gold text-deep-black p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-semibold mb-3">24/7 Emergency Service</h3>
                <p className="mb-4">Need immediate transportation? Call our emergency line for urgent requests.</p>
                <div className="text-2xl font-bold">
                  <a href="tel:+12814286921" className="hover:underline">(281) 428-6921</a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white text-deep-black p-8 rounded-lg shadow-2xl animate-fade-in-up delay-300">
              <h3 className="text-2xl font-playfair font-semibold mb-6">Request a Quote</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold mb-2">
                      First Name*
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold mb-2">
                      Last Name*
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-2">
                    Email Address*
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                    placeholder="john.doe@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold mb-2">
                    Phone Number*
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="serviceType" className="block text-sm font-semibold mb-2">
                      Service Type*
                    </label>
                    <select
                      id="serviceType"
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleServiceTypeChange}
                      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                    >
                      <option value="" disabled>Select a service</option>
                      <option value="airport">Airport Transfer</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="special">Special Occasion</option>
                      <option value="hourly">Hourly Service</option>
                      <option value="custom">Custom Request</option>
                    </select>
                    {errors.serviceType && (
                      <p className="text-red-500 text-sm mt-1">{errors.serviceType}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="dateNeeded" className="block text-sm font-semibold mb-2">
                      Date Needed
                    </label>
                    <input
                      id="dateNeeded"
                      type="date"
                      name="dateNeeded"
                      value={formData.dateNeeded}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pickupLocation" className="block text-sm font-semibold mb-2">
                      Pickup Location
                    </label>
                    <input
                      id="pickupLocation"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div>
                    <label htmlFor="destination" className="block text-sm font-semibold mb-2">
                      Destination
                    </label>
                    <input
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                      placeholder="456 Airport Blvd"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="additionalDetails" className="block text-sm font-semibold mb-2">
                    Additional Details
                  </label>
                  <textarea
                    id="additionalDetails"
                    name="additionalDetails"
                    rows={4}
                    value={formData.additionalDetails}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-luxury-gold"
                    placeholder="Please provide any additional details about your transportation needs..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-luxury-gold text-deep-black hover:bg-yellow-600 font-semibold py-4 text-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                >
                  <Send className="mr-2 h-5 w-5" />
                  {isSubmitting ? 'Sending...' : 'Send Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}